import React, { useState, useEffect, useCallback } from 'react';
import useXHR from '../../hooks/useXHR';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ProtocolSharingMarketplace - Discover, import, and publish search protocols.
 * Shows community-shared protocols with anonymized performance stats.
 * One-click import into your own categories.
 */
const ProtocolSharingMarketplace = ({ showToast, onProtocolImported }) => {
  const { token } = useAuth();
  const xhr = useXHR(token);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingId, setImportingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);

  const loadProtocols = useCallback(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    xhr.get('/protocol-marketplace', {
      onSuccess: (data) => { setProtocols(data.protocols || []); },
      onFinally: () => setLoading(false)
    });
  }, [xhr, token]);

  useEffect(() => { loadProtocols(); }, [loadProtocols]);

  const handleImport = (protocolId, protocolName) => {
    setImportingId(protocolId);
    xhr.post(`/protocol-marketplace/import/${protocolId}`, {
      onSuccess: (data) => {
        showToast && showToast(data.message || `Imported "${protocolName}"!`, 'success');
        onProtocolImported && onProtocolImported(data.category);
        loadProtocols();
      },
      onError: (errMsg) => { showToast && showToast(errMsg || 'Import failed', 'error'); },
      onFinally: () => setImportingId(null)
    });
  };

  const handleCopyProtocol = (protocol, includeContents) => {
    setCopyingId(`${protocol.id}-${includeContents ? 'full' : 'cat'}`);
    xhr.post('/categories/copy-protocol', {
      body: {
        source_category_id: protocol.category_id || protocol.id,
        include_contents: includeContents,
        new_name: protocol.name
      },
      onSuccess: (data) => {
        const mode = includeContents ? 'with contents' : 'category only';
        showToast && showToast(data.message || `Copied "${protocol.name}" (${mode})!`, 'success');
        onProtocolImported && onProtocolImported(data.category);
      },
      onError: (errMsg) => { showToast && showToast(errMsg || 'Copy failed', 'error'); },
      onFinally: () => setCopyingId(null)
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: '#666', fontSize: '0.7rem' }}>
        Loading community protocols...
      </div>
    );
  }

  return (
    <div data-testid="protocol-sharing-marketplace">
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 12px', borderBottom: '1px solid rgba(252, 211, 77,0.1)'
      }}>
        <div>
          <div style={{ color: '#a5b4fc', fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="fas fa-store" style={{ fontSize: '0.75rem' }} />
            Protocol Sharing Marketplace
          </div>
          <div style={{ color: '#555', fontSize: '0.48rem', marginTop: 2 }}>
            Import proven protocols with one click. Publish yours to help others!
          </div>
        </div>
        <button
          onClick={loadProtocols}
          style={{
            background: 'rgba(252, 211, 77,0.1)', border: '1px solid rgba(252, 211, 77,0.2)',
            color: '#818cf8', padding: '4px 10px', borderRadius: 6, fontSize: '0.6rem',
            cursor: 'pointer', fontWeight: 600
          }}
          data-testid="refresh-marketplace"
        >Refresh</button>
      </div>

      {/* Protocol List */}
      <div style={{ maxHeight: 400, overflowY: 'auto', padding: '6px 8px' }}>
        {protocols.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>No community protocols yet</div>
            <div style={{ fontSize: '0.55rem', color: '#555' }}>
              Be the first! Set a category to "Public" to share your protocol with the community.
            </div>
          </div>
        ) : (
          protocols.map((p, i) => (
            <div
              key={p.id}
              data-testid={`marketplace-protocol-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
                transition: 'background 0.15s',
                borderRadius: 6
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(252, 211, 77,0.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Rank */}
              <span style={{
                color: i < 3 ? '#fbbf24' : '#555', fontSize: '0.6rem', fontWeight: 700,
                minWidth: 18, textAlign: 'right'
              }}>
                {i < 3 ? ['1st', '2nd', '3rd'][i] : `${i + 1}.`}
              </span>

              {/* Protocol Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#334155', fontSize: '0.72rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </span>
                  {p.is_own && (
                    <span style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', padding: '0px 5px', borderRadius: 8, fontSize: '0.45rem', fontWeight: 700 }}>YOURS</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                  <span style={{ color: '#888', fontSize: '0.55rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}
                    title={`Protocol: ${p.protocol}`}
                  >{p.protocol}</span>
                  <span style={{ color: '#555', fontSize: '0.48rem' }}>by {p.author}</span>
                </div>
              </div>

              {/* Stats & Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{
                  background: p.result_count >= 100 ? 'rgba(16,185,129,0.15)' : p.result_count >= 10 ? 'rgba(245,158,11,0.15)' : 'rgba(255, 255, 255, 0.85)',
                  color: p.result_count >= 100 ? '#34d399' : p.result_count >= 10 ? '#fbbf24' : '#888',
                  padding: '2px 8px', borderRadius: 10, fontSize: '0.52rem', fontWeight: 600
                }}>
                  {p.result_count} URLs
                </span>

                {/* Copy Category button */}
                {!p.is_own && (
                  <button
                    onClick={() => handleCopyProtocol(p, false)}
                    disabled={copyingId === `${p.id}-cat`}
                    data-testid={`copy-category-${i}`}
                    style={{
                      background: copyingId === `${p.id}-cat` ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.12)',
                      border: '1px solid rgba(245,158,11,0.3)',
                      color: '#047857', padding: '3px 8px', borderRadius: 6,
                      fontSize: '0.5rem', fontWeight: 700, cursor: copyingId === `${p.id}-cat` ? 'wait' : 'pointer',
                      transition: 'all 0.15s', whiteSpace: 'nowrap'
                    }}
                    title="Copy just the category name and protocol rules"
                  >
                    {copyingId === `${p.id}-cat` ? '...' : 'Copy Category'}
                  </button>
                )}

                {/* Copy Category & Contents button */}
                {!p.is_own && (
                  <button
                    onClick={() => handleCopyProtocol(p, true)}
                    disabled={copyingId === `${p.id}-full`}
                    data-testid={`copy-category-contents-${i}`}
                    style={{
                      background: copyingId === `${p.id}-full` ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.12)',
                      border: '1px solid rgba(16,185,129,0.3)',
                      color: '#34d399', padding: '3px 8px', borderRadius: 6,
                      fontSize: '0.5rem', fontWeight: 700, cursor: copyingId === `${p.id}-full` ? 'wait' : 'pointer',
                      transition: 'all 0.15s', whiteSpace: 'nowrap'
                    }}
                    title="Copy the category rules AND all associated URLs"
                  >
                    {copyingId === `${p.id}-full` ? '...' : 'Copy + URLs'}
                  </button>
                )}

                {/* Import button */}
                {!p.is_own && (
                  <button
                    onClick={() => handleImport(p.id, p.name)}
                    disabled={importingId === p.id}
                    data-testid={`import-protocol-${i}`}
                    style={{
                      background: importingId === p.id ? 'rgba(252, 211, 77,0.3)' : 'rgba(252, 211, 77,0.15)',
                      border: '1px solid rgba(252, 211, 77,0.3)',
                      color: '#a5b4fc', padding: '3px 8px', borderRadius: 6,
                      fontSize: '0.5rem', fontWeight: 700, cursor: importingId === p.id ? 'wait' : 'pointer',
                      transition: 'all 0.15s', whiteSpace: 'nowrap'
                    }}
                    title={`Import "${p.name}" protocol into your categories`}
                  >
                    {importingId === p.id ? '...' : 'Import'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom microfont */}
      <div style={{ padding: '4px 10px', color: '#3a3a3a', fontSize: '0.42rem', textAlign: 'center', borderTop: '1px solid rgba(255, 255, 255, 0.85)' }}>
        Protocols ranked by URL count. Import copies the protocol to your private categories. Publish by setting category privacy to "Public".
      </div>
    </div>
  );
};

export default ProtocolSharingMarketplace;
